'use client'

// imports
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

// lib files
import { handleRecommendedShowClick } from '@/lib/content-services/rec-engine';
import { ProfileData } from '@/lib/types/api/profile-data';
import { recommendedShowsAtom, getGenreRecommendedShowsAtom } from '@/lib/atoms/recommended-shows';
import { RECOMMENDED_SHOWS_FEATURE_CATEGORY } from "@/lib/constants";
import safeFetch from '@/lib/helpers/safe-fetch';
import { sendPbsGtmEvent } from "@/lib/helpers/send-pbs-gtm-event";
import type { ShowBadgeFlags } from '@/components/ShowPoster/ShowPoster';
import { ShowRowContent } from '@/lib/types/api/show-data';
import { useHasMounted } from '@/lib/hooks';
import { useIdentity } from '@/lib/hooks/useIdentity';

// components
import ShowRow from "@/components/ShowRow/ShowRow";

interface RecommendedShowsRowProps {
  depTopPicksShowData?: ShowRowContent[];
  fallback?: React.ReactNode;
  genre?: string;
  useMainRecommendedShows?: boolean;
  profileStub?: ProfileData;
  showBadgeFlags?: ShowBadgeFlags;
}

const getApiRoute = (genre?: string, useMainRecommendedShows?: boolean): string | null => {
  switch(true) {
    // mysteries has it's own dedicated endpoint
    case genre === 'mystery':
      return `/api/profile/recommended-mysteries/`;
    // genre is defined, so we call it with a query param
    case genre !== undefined:
      return `/api/profile/recommended-shows/?genre=${genre}`;
    // when we want the main recommended shows endpoint
    case useMainRecommendedShows === true:
      return `/api/profile/recommended-shows/`;
    default:
      return null;
  }
}

const RecommendedShowsRow = (props: RecommendedShowsRowProps) => {
  const {
    depTopPicksShowData,
    fallback,
    genre,
    useMainRecommendedShows,
    profileStub,
    showBadgeFlags,
  } = props;
  const hasMounted = useHasMounted();
  const { isAuthenticated, isSignedIn, cachedIdentity } = useIdentity();

  const userFirstName = profileStub ? profileStub.profile.first_name : cachedIdentity?.firstName;

  const atom = useMemo(
    () => genre ? getGenreRecommendedShowsAtom(genre) : recommendedShowsAtom,
    [genre]
  );

  const [recommendedShowsData, setRecommendedShowsData] = useAtom(atom);
  const recommendedShowsApiRoute = getApiRoute(genre, useMainRecommendedShows);

  useEffect(() => {
    const fetchRecommendedShowsData = async (recommendedShowsApiRoute: string) => {
      try {
        const response = await safeFetch(recommendedShowsApiRoute);
        const json = await response.json();
        const topPicksResponse = json.recommendedShowsData;
        setRecommendedShowsData(topPicksResponse);
      } catch (error) {
        console.error(error);
      }
    }

    if (depTopPicksShowData) {
      setRecommendedShowsData(depTopPicksShowData)
    } else if (isAuthenticated && recommendedShowsApiRoute) {
      fetchRecommendedShowsData(recommendedShowsApiRoute);
    }
  }, [
    depTopPicksShowData,
    isAuthenticated,
    recommendedShowsApiRoute,
    setRecommendedShowsData,
  ]);

  const handleRecommendedShowPosterClick = (show: ShowRowContent, index?: number) => {
    handleRecommendedShowClick(show);
    sendPbsGtmEvent({
      feature_category: RECOMMENDED_SHOWS_FEATURE_CATEGORY,
      feature_name: "recommended show poster",
      object_action_behavior: "user navigates to show page",
      object_action: "click",
      object_location: genre ? `${genre} explore hub recommended show row` : "homepage recommended show row",
      object_name: "show poster",
      // This check is rather elaborate because 0 is considered falsey
      object_position: index !== undefined ? index + 1 : undefined,
      object_type: "link",
      object_url: `/show/${show.slug}/`,
      pbs_content_id: show.slug,
      show_title: show.title,
      signed_in_status: isSignedIn,
    });
  }

  // Render the fallback during SSR and the first client render, so signed-out
  // visitors get server HTML instead of an empty slot. Identical markup on both
  // passes, so hydration still matches.
  // If there is no genre OR we are not fetching the main recommended shows,
  // in which case the recommendedShowsApiRoute is null,
  // render the fallback.
  if (!hasMounted || !recommendedShowsApiRoute) {
    return <>{fallback}</>;
  }

  const hasRecommendations =
    (isSignedIn || Boolean(profileStub)) &&
    recommendedShowsData &&
    recommendedShowsData.length > 0;

  if (hasRecommendations) {
    return (
      <ShowRow
        shows={recommendedShowsData}
        title={`Top Picks for ${userFirstName ? userFirstName : "You"}`}
        onShowPosterClick={handleRecommendedShowPosterClick}
        showBadgeFlags={showBadgeFlags}
      />
    );
  }

  // If a fallback react node is supplied, render that when there are no recommended shows
  return <>{fallback}</>;
};

export default RecommendedShowsRow;
